// mantine
import { Button, TextInput, UnstyledButton } from '@mantine/core';
// utils
import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import { TLibrary, TLibraryData } from '@newo/api/gateway';
import validationSchema from '@/pages/Libraries/components/LibraryForm/LibraryForm.schema.ts';
import {
    useCreateLibraryMutation,
    useUpdateLibraryMutation
} from '@/services/designer/libraries/libraries.queries.ts';

type LibraryFormProps = {
    library?: TLibrary;
    projectId: string;
    onClose?: () => void;
    onSubmit?: () => void;
};

const LibraryForm = ({
    library,
    projectId,
    onClose,
    onSubmit
}: LibraryFormProps) => {
    const { mutate: createLibrary } = useCreateLibraryMutation();
    const { mutate: updateLibrary } = useUpdateLibraryMutation();

    const initialValues: TLibraryData = {
        idn: library?.idn ?? ''
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            if (library) {
                const data = processFormData(formData, true);
                updateLibrary(
                    { projectId, libraryId: library.id, data },
                    {
                        onSuccess() {
                            onSubmit?.();
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
                return;
            }
            const data = processFormData(formData, true);
            if (!projectId) {
                throw new Error('Project ID is required');
            }
            createLibrary(
                { projectId, data },
                {
                    onSuccess() {
                        onSubmit?.();
                        onClose?.();
                    },
                    onSettled() {
                        formik.setSubmitting(false);
                    }
                }
            );
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Idn"
                name="idn"
                placeholder="Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {library ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default LibraryForm;
